import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import OriginalGalleryView from '../components/Gallery/OriginalGalleryView';
import SEOHead from '../components/SEO/SEOHead';
import sitePaths from '../utils/sitePaths';
import categoryContent from '../utils/categoryContent';
import './SharedGalleryPage.css';

type GalleryCategoryFilter = {
  slug: string;
  name: string;
  h1?: string;
  title?: string;
  description?: string;
  intro?: string;
  body?: string;
  faqs?: { question: string; answer: string }[];
  fromCategory?: boolean;
};

const GalleryPage: React.FC = () => {
  const { category: categoryParam = '' } = useParams();
  const builtin = sitePaths.filterForSlug(categoryParam);
  const [filter, setFilter] = useState<GalleryCategoryFilter | null>(builtin);
  const [heading, setHeading] = useState(filter?.h1 || '');

  useEffect(() => {
    let cancelled = false;
    setFilter(sitePaths.filterForSlug(categoryParam));
    import('../utils/CategoryAPI').then(({ loadLiveCategories }) => loadLiveCategories()).then((categories) => {
      if (cancelled) return;
      const resolved = sitePaths.resolveGalleryCategory(categoryParam, categories);
      if (resolved) setFilter(resolved);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [categoryParam]);

  const pageTitle = filter && filter.h1 ? filter.h1 : heading;
  const introHtml = filter ? categoryContent.renderIntroHtml(filter.intro) : '';
  const restHtml = filter ? categoryContent.renderBodyAndFaqHtml(filter) : '';
  const faqJson = filter ? categoryContent.faqPageJson(filter.faqs) : '';

  return (
    <div className="gallery-page" data-route="/galleries" data-track-source="gallery_category">
      <SEOHead
        exactTitle={Boolean(filter)}
        suppressDefaults={Boolean(filter)}
        title={filter ? (filter.title || '') : `${heading} - Photography Portfolio`}
        description={filter ? (filter.description || '') : 'Explore my collection of photography work including portraits, weddings, events, and lifestyle photography. View my portfolio of beautiful, artistic images.'}
        canonicalUrl={filter ? `https://carmenlorraine.com/galleries/${filter.slug}` : 'https://carmenlorraine.com/galleries'}
        keywords={['photo galleries', 'photography portfolio', 'portrait gallery', 'wedding photography', 'event photography', 'Cape Town photography']}
        type="website"
        jsonLd={faqJson ? [faqJson] : undefined}
      />
      <main className="page-content">
        <section className="galleries-section">
          <div className="galleries-container">
            <div className="galleries-header">
              <div className="header-content">
                <h1 className="galleries-title">{pageTitle}</h1>
                {filter ? null : (
                  <p className="galleries-subtitle">Explore my collection of photography work</p>
                )}
              </div>
              <div className="header-action" id="gallery-page-share-slot" />
            </div>
            {introHtml ? (
              <div className="galleries-intro" dangerouslySetInnerHTML={{ __html: introHtml }} />
            ) : null}
            <OriginalGalleryView onGalleryHeadingChange={setHeading} />
            {restHtml ? (
              <>
                <div className="category-break" aria-hidden="true">
                  <span />
                  <img src={`${process.env.PUBLIC_URL || ''}/carmen-logo.png`} alt="" />
                  <span />
                </div>
                <section className="category-copy">
                  <div className="category-copy-frame" dangerouslySetInnerHTML={{ __html: restHtml }} />
                </section>
                <div className="category-end" aria-hidden="true" />
              </>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
};

export default GalleryPage;

